import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { parseDataManifest, verifiedDataResponse, type DataManifest } from './nativeProgramData';

const DATA_ORIGIN = 'https://tercih360.com.tr';
const CACHE_NAME = 'tercih360-native-data-v1';

/** Only public data is refreshed. Executable app code always remains bundled. */
export async function initializeNativeData(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const bundledFetch = window.fetch.bind(window);
  let bundled: DataManifest | null = null;
  try { bundled = parseDataManifest(await (await bundledFetch('/data/mobile-manifest.json')).json()); } catch { /* optional */ }
  let manifest: DataManifest | null = null;
  try { manifest = parseDataManifest(JSON.parse(localStorage.getItem(CACHE_NAME) || 'null')); } catch { /* use bundled data */ }
  const check = (async () => {
    try {
      const response = await CapacitorHttp.get({ url: `${DATA_ORIGIN}/data/mobile-manifest.json`, responseType: 'json', connectTimeout: 1500, readTimeout: 1500, headers: { 'Cache-Control': 'no-cache' } });
      const next = response.status === 200 ? parseDataManifest(response.data) : null;
      if (next) {
        manifest = next;
        try { localStorage.setItem(CACHE_NAME, JSON.stringify(next)); } catch { /* storage optional */ }
      }
    } catch { /* retain last known version */ }
  })();
  const ready = Promise.race([check, new Promise<void>(resolve => setTimeout(resolve, 1600))]);
  const pending = new Map<string, Promise<Response>>();
  window.fetch = async (input, init) => {
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, location.origin);
    if (url.origin !== location.origin || !url.pathname.startsWith('/data/') || (init?.method && init.method !== 'GET') || (input instanceof Request && input.method !== 'GET')) return bundledFetch(input, init);
    await ready;
    const hash = manifest?.files[url.pathname];
    if (!hash || hash === bundled?.files[url.pathname]) return bundledFetch(input, init);
    const key = `${url.pathname}?sha256=${hash}`;
    let loading = pending.get(key);
    if (!loading) {
      loading = (async () => {
        let cache: Cache | null = null;
        try {
          cache = await caches.open(CACHE_NAME);
          const saved = await cache.match(key);
          if (saved) return saved;
        } catch { /* caching unavailable */ }
        try {
          // .bin preserves original JSON/gzip bytes; Capacitor otherwise parses JSON before hashing.
          const remote = await CapacitorHttp.get({ url: `${DATA_ORIGIN}/mobile-data/${url.pathname.slice(6)}.bin?sha256=${hash}`, responseType: 'arraybuffer', connectTimeout: 3000, readTimeout: 8000 });
          if (remote.status !== 200 || typeof remote.data !== 'string') throw new Error('Invalid data response');
          const bytes = Uint8Array.from(atob(remote.data), c => c.charCodeAt(0));
          const verified = await verifiedDataResponse(bytes, hash, url.pathname);
          if (!verified) throw new Error('Data verification failed');
          if (cache) {
            await cache.put(key, verified.clone());
            for (const old of await cache.keys()) if (new URL(old.url).pathname === url.pathname && old.url !== new URL(key, location.origin).href) await cache.delete(old);
          }
          return verified;
        } catch {
          if (cache) {
            for (const old of await cache.keys()) {
              if (new URL(old.url).pathname === url.pathname) {
                const previous = await cache.match(old);
                if (previous) return previous;
              }
            }
          }
          return bundledFetch(input, init);
        }
      })();
      pending.set(key, loading);
    }
    return (await loading).clone();
  };
}